import React from 'react';
import { useUser } from '@/hooks';
import styles from './LoginProviders.module.scss';
import { t } from 'i18next';
import { ELoginProvider, ELoginProvidersViewType } from '@/models';
import clsx from 'clsx';
import {
  Button,
  EButtonHierarchy,
  EButtonSize,
  EButtonStyle,
} from '@/components/common/Button/Button';
import { PublicImage } from '@/components/common/PublicImage/PublicImage';
import { useDispatch } from 'react-redux';
import { showPopupMessage } from '@/store/reducers';
import { ERROR_CODES_FIREBASE } from '@/constants/integrations/firebase';
import { IMAGES } from '@/constants/ui/images';

interface ILoginProvider {
  type: ELoginProvider;
  icon: string;
  text: string;
}
interface ILoginProviderProps {
  onLoginSuccess?: () => void;
  loginProvidersViewType: ELoginProvidersViewType;
  source: string;
  displayVendorName?: boolean;
  isModal?: boolean;
}
export const LoginProviders: React.FC<ILoginProviderProps> = props => {
  const userHook = useUser();
  const dispatch = useDispatch();
  const providers: ILoginProvider[] = [
    {
      type: ELoginProvider.google,
      icon: 'google',
      text: t('loginProviders.google'),
    },
    {
      type: ELoginProvider.facebook,
      icon: 'facebook',
      text: t('loginProviders.facebook'),
    },
    {
      type: ELoginProvider.apple,
      icon: 'apple',
      text: t('loginProviders.apple'),
    },
  ];

  const getProviderIcon = (prov: ILoginProvider) => {
    return `${IMAGES.PATHS.LOGIN_PROVIDERS}/${prov.icon}.png`;
  };

  const loginWithProvider = async (provider: ILoginProvider) => {
    try {
      await userHook.loginWithProvider(provider.type, props.source);
      props.onLoginSuccess?.();
    } catch (error: any) {
      const errorCode = error?.code || '';

      // Only show error popup for specific errors that require user action
      if (errorCode === ERROR_CODES_FIREBASE.ACCOUNT_EXISTS) {
        dispatch(
          showPopupMessage({
            iconSrc: IMAGES.EXCLAMATION,
            titleKey: 'loginProviders.error.accountExists.title',
            subtitleKey: 'loginProviders.error.accountExists.subtitle',
            buttonTextKey: 'loginProviders.error.accountExists.buttonText',
          })
        );
      }

      // For other errors (popup-closed-by-user, cancelled-popup-request, etc.) - don't show popup
      // These are expected user behaviors or internal state management, not actual errors
    }
  };

  const providerButtonStyle = props.isModal
    ? EButtonStyle.tonal
    : EButtonStyle.outlined;
  return (
    <div
      className={clsx(
        styles.loginProviders,
        props.loginProvidersViewType === ELoginProvidersViewType.horizontal &&
          styles.horizontal
      )}
    >
      {providers.map(provider => (
        <div className={styles.provider} key={provider.type}>
          <Button
            fillWidth={true}
            size={EButtonSize.lg}
            hierarchy={EButtonHierarchy.primary}
            buttonStyle={providerButtonStyle}
            onClick={() => {
              loginWithProvider(provider);
            }}
          >
            <div className={styles.providerContentWrapper}>
              <PublicImage
                className={styles.providerIcon}
                src={getProviderIcon(provider)}
                alt={provider.text}
              />
              {props.displayVendorName && (
                <div className={styles.providerText}>{provider.text}</div>
              )}
            </div>
          </Button>
        </div>
      ))}
    </div>
  );
};
